import React, { useState, useRef, useEffect } from "react";

// ── Props ─────────────────────────────────────────────────────
interface SidebarDropdownProps {
  label: string;
  options: string[];
  value: string;
  onChange?: (value: string) => void;
}

const SidebarDropdown: React.FC<SidebarDropdownProps> = ({
  label,
  options,
  value,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (option: string) => {
    setIsOpen(false);
    onChange?.(option);
  };

  return (
    <div className="sdd-group" ref={containerRef}>
      {/* Label row — icons removed per design */}
      <div className="sdd-label-row">
        <span className="sdd-label">{label}</span>
      </div>

      {/* Control */}
      <div
        className={`sdd-control${isOpen ? " sdd-control--open" : ""}`}
        onClick={() => setIsOpen((o) => !o)}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="sdd-value">{value}</span>
        <svg
          className={`sdd-chevron${isOpen ? " sdd-chevron--up" : ""}`}
          width="11"
          height="11"
          viewBox="0 0 11 11"
          fill="none"
        >
          <path
            d="M2 4L5.5 7.5L9 4"
            stroke="#94a3b8"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {/* Menu */}
      {isOpen && (
        <ul className="sdd-menu" role="listbox">
          {options.map((opt) => (
            <li
              key={opt}
              className={`sdd-option${opt === value ? " sdd-option--active" : ""}`}
              role="option"
              aria-selected={opt === value}
              onClick={() => handleSelect(opt)}
            >
              <span className="sdd-option-check">
                {opt === value && (
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path
                      d="M1.5 5L3.8 7.5L8.5 2"
                      stroke="#1F8CF9"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </span>
              {opt}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SidebarDropdown;
